import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError, videoUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isProTier } from '@/lib/tier';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '@/components/ui/select';
import {
  Columns2, Play, Pause, RotateCcw, Lock, Loader2, Trophy, Minus, ArrowLeft, Video as VideoIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const SCORE_KEYS = ['body_position', 'kite_control', 'board_edge', 'timing', 'style'];

const overallOf = (a) => {
  const s = a?.result?.scores;
  if (!s || !Object.keys(s).length) return null;
  return Math.round((Object.values(s).reduce((x, y) => x + y, 0) / Object.keys(s).length) * 10) / 10;
};

const fmtTime = (s) => {
  if (!s && s !== 0) return '0:00';
  const sec = Math.max(0, Math.floor(s));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

const labelFor = (a) => {
  const d = (a.created_at || '').slice(0, 10);
  const spot = a.meta?.spot_name ? ` · ${a.meta.spot_name}` : '';
  const ov = overallOf(a);
  return `${d}${spot}${ov != null ? ` · ${ov}/10` : ''}`;
};

const SidePicker = ({ label, value, onChange, sessions, references, testid }) => {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-w-0">
      <div className="label-eyebrow text-muted-foreground mb-1.5">{label}</div>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="bg-[#12161B] border-white/15" data-testid={testid}>
          <SelectValue placeholder={t('compare.chooseSession')} />
        </SelectTrigger>
        <SelectContent>
          {sessions.length > 0 && (
            <SelectGroup>
              <SelectLabel>{t('compare.yourSessions')}</SelectLabel>
              {sessions.map((o) => (<SelectItem key={o.id} value={o.id}>{labelFor(o)}</SelectItem>))}
            </SelectGroup>
          )}
          {references.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-[#FFB020]">{t('compare.referenceClips')}</SelectLabel>
              {references.map((o) => (
                <SelectItem key={`ref-${o.id}`} value={o.id}>
                  {`\u2605 ${o.reference_name || t('compare.reference')}${overallOf(o) != null ? ` \u00b7 ${overallOf(o)}/10` : ''}`}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};

const ScorePanel = ({ a, side }) => {
  const { t } = useTranslation();
  const ov = overallOf(a);
  return (
    <div className="kite-card p-4" data-testid={`compare-meta-${side}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-lg truncate">{(a.result?.skill_level_detected || t('compare.sessionFallback'))}</div>
          <div className="text-xs text-muted-foreground truncate">{labelFor(a)}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-data text-2xl font-semibold text-[#00B4D8]">{ov != null ? ov : '—'}<span className="text-sm text-muted-foreground">/10</span></div>
          <div className="label-eyebrow text-muted-foreground">{t('compare.overall')}</div>
        </div>
      </div>
    </div>
  );
};

const VideoPane = ({ a, side, refEl, onLoaded, onTime, onEnded }) => {
  const { t } = useTranslation();
  return (
    <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10">
      <div className="absolute top-2 left-2 z-10"><Badge className="bg-black/60 text-white border border-white/10">{side === 'left' ? 'A' : 'B'}</Badge></div>
      {a.video_available ? (
        <video
          ref={refEl}
          src={videoUrl(a.id)}
          poster={a.thumbnail || undefined}
          playsInline
          muted
          preload="metadata"
          className="w-full aspect-video bg-black"
          onLoadedMetadata={onLoaded}
          onTimeUpdate={onTime}
          onEnded={onEnded}
          data-testid={`compare-video-${side}`}
        />
      ) : (
        <div className="w-full aspect-video bg-black flex flex-col items-center justify-center text-muted-foreground">
          {a.thumbnail && <img src={a.thumbnail} alt="" className="absolute opacity-30 w-full h-full object-cover" />}
          <VideoIcon className="w-7 h-7 relative" />
          <span className="text-xs mt-2 relative">{t('compare.videoExpired')}</span>
        </div>
      )}
    </div>
  );
};

export default function Compare() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isPro = isProTier(user?.subscription_tier);

  const [list, setList] = useState(null);
  const [refs, setRefs] = useState([]);
  const [leftId, setLeftId] = useState(params.get('left') || '');
  const [rightId, setRightId] = useState(params.get('right') || '');
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);
  const [loadingPair, setLoadingPair] = useState(false);

  // playback sync
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [maxDur, setMaxDur] = useState(0);

  useEffect(() => {
    if (!isPro) return;
    (async () => {
      try {
        const [own, ref] = await Promise.all([
          api.get('/analyses'),
          api.get('/analyses/reference-clips').catch(() => ({ data: [] })),
        ]);
        setList((own.data || []).filter((a) => a.status === 'completed'));
        setRefs(ref.data || []);
      } catch (e) { toast.error(apiError(e, t('compare.couldNotLoadSessions'))); setList([]); }
    })();
  }, [isPro, t]);

  // keep URL in sync
  useEffect(() => {
    const next = {};
    if (leftId) next.left = leftId;
    if (rightId) next.right = rightId;
    setParams(next, { replace: true });
  }, [leftId, rightId, setParams]);

  // fetch the selected pair
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!leftId || !rightId) { setLeft(null); setRight(null); return; }
      setLoadingPair(true);
      try {
        const [l, r] = await Promise.all([api.get(`/analyses/${leftId}`), api.get(`/analyses/${rightId}`)]);
        if (!active) return;
        setLeft(l.data); setRight(r.data);
        setPlaying(false); setProgress(0); setMaxDur(0);
      } catch (e) {
        if (active) toast.error(apiError(e, t('compare.couldNotLoadComparison')));
      } finally {
        if (active) setLoadingPair(false);
      }
    };
    run();
    return () => { active = false; };
  }, [leftId, rightId, t]);

  const togglePlay = () => {
    const l = leftRef.current, r = rightRef.current;
    if (playing) { l && l.pause(); r && r.pause(); setPlaying(false); }
    else { l && l.play().catch(() => {}); r && r.play().catch(() => {}); setPlaying(true); }
  };
  const restart = () => {
    const l = leftRef.current, r = rightRef.current;
    if (l) l.currentTime = 0; if (r) r.currentTime = 0; setProgress(0);
  };
  const seek = (val) => {
    const tv = Array.isArray(val) ? val[0] : val;
    const l = leftRef.current, r = rightRef.current;
    if (l && isFinite(l.duration)) l.currentTime = Math.min(tv, l.duration);
    if (r && isFinite(r.duration)) r.currentTime = Math.min(tv, r.duration);
    setProgress(tv);
  };
  const onLoaded = () => {
    const ld = leftRef.current?.duration || 0;
    const rd = rightRef.current?.duration || 0;
    setMaxDur(Math.max(isFinite(ld) ? ld : 0, isFinite(rd) ? rd : 0));
  };
  const onTime = () => { const l = leftRef.current; if (l) setProgress(l.currentTime); };
  const onEnded = () => { setPlaying(false); leftRef.current?.pause(); rightRef.current?.pause(); };

  const comparison = useMemo(() => {
    if (!left || !right) return [];
    const ls = left.result?.scores || {};
    const rs = right.result?.scores || {};
    return SCORE_KEYS.map((k) => ({ key: k, label: t(`compare.scores.${k}`), l: ls[k] ?? null, r: rs[k] ?? null }));
  }, [left, right, t]);

  // reference clips not already in the user's own list (avoid duplicate option values)
  const refsAvailable = useMemo(
    () => (refs || []).filter((r) => !(list || []).some((o) => o.id === r.id)),
    [refs, list],
  );

  // ---- Pro paywall ----
  if (!isPro) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF6B35]/10 mb-2"><Columns2 className="w-8 h-8 text-[#FF6B35]" /></div>
        <div className="label-eyebrow text-[#FF6B35] mt-5">{t('compare.paywallEyebrow')}</div>
        <h1 className="font-display text-4xl mt-2">{t('compare.paywallTitle')}</h1>
        <p className="text-muted-foreground mt-3">{t('compare.paywallDesc')}</p>
        <Button onClick={() => navigate('/pricing')} className="mt-7 bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-xl h-12 px-8" data-testid="compare-upgrade">
          <Lock className="w-4 h-4 mr-2" /> {t('compare.unlockWithPro')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-2"><Columns2 className="w-4 h-4" /> {t('compare.proEyebrow')}</div>
      <h1 className="font-display text-4xl sm:text-5xl">{t('compare.title')}</h1>
      <p className="text-muted-foreground mt-2">{t('compare.subtitle')}</p>

      {/* Pickers */}
      <div className="kite-card p-4 mt-6 flex flex-col sm:flex-row gap-4 items-end">
        {list === null ? (
          <Skeleton className="h-12 w-full bg-white/10 rounded-xl" />
        ) : (list.length + refsAvailable.length) < 2 ? (
          <div className="text-muted-foreground text-sm">
            {t('compare.needTwo')} <Link to="/analyze" className="text-[#48CAE4]">{t('compare.analyzeSessionLink')}</Link>
          </div>
        ) : (
          <>
            <SidePicker label={t('compare.sessionA')} value={leftId} onChange={setLeftId} sessions={list} references={refsAvailable} testid="compare-pick-left" />
            <Columns2 className="w-5 h-5 text-muted-foreground hidden sm:block mb-2.5" />
            <SidePicker label={t('compare.sessionB')} value={rightId} onChange={setRightId} sessions={list} references={refsAvailable} testid="compare-pick-right" />
          </>
        )}
      </div>

      {loadingPair && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" /></div>}

      {!loadingPair && left && right && (
        <>
          {/* Videos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <VideoPane a={left} side="left" refEl={leftRef} onLoaded={onLoaded} onTime={onTime} onEnded={onEnded} />
            <VideoPane a={right} side="right" refEl={rightRef} onLoaded={onLoaded} onTime={onTime} onEnded={onEnded} />
          </div>

          {/* Sync controls */}
          <div className="kite-card p-4 mt-4 flex items-center gap-4" data-testid="compare-controls">
            <Button size="icon" onClick={togglePlay} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black rounded-full shrink-0" data-testid="compare-playpause" aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <Button size="icon" variant="outline" onClick={restart} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-full shrink-0" data-testid="compare-restart" aria-label="Restart">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <span className="font-data text-xs text-muted-foreground w-10 text-right">{fmtTime(progress)}</span>
            <Slider value={[Math.min(progress, maxDur || 0)]} max={maxDur || 1} step={0.1} onValueChange={seek} className="flex-1" data-testid="compare-seek" />
            <span className="font-data text-xs text-muted-foreground w-10">{fmtTime(maxDur)}</span>
          </div>

          {/* Score comparison */}
          <div className="kite-card p-5 mt-4">
            <h3 className="font-display text-xl flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFB020]" /> {t('compare.techniqueCompared')}</h3>
            <div className="grid grid-cols-2 gap-3 mt-4 md:hidden">
              <ScorePanel a={left} side="left" />
              <ScorePanel a={right} side="right" />
            </div>
            <div className="mt-5 space-y-3" data-testid="compare-scores">
              {comparison.map(({ key, label, l, r }) => {
                const lw = l != null && r != null && l > r;
                const rw = l != null && r != null && r > l;
                return (
                  <div key={key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3" data-testid={`compare-row-${key}`}>
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-2 rounded-full bg-[#00B4D8]/80" style={{ width: `${(l || 0) * 9}px` }} />
                      <span className={`font-data text-sm w-6 text-right ${lw ? 'text-[#2EE59D] font-bold' : 'text-white'}`}>{l != null ? l : '—'}</span>
                    </div>
                    <div className="text-xs text-muted-foreground text-center w-24 truncate">{label}</div>
                    <div className="flex items-center gap-2">
                      <span className={`font-data text-sm w-6 ${rw ? 'text-[#2EE59D] font-bold' : 'text-white'}`}>{r != null ? r : '—'}</span>
                      <div className="h-2 rounded-full bg-[#FF6B35]/80" style={{ width: `${(r || 0) * 9}px` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10 text-sm">
              <span className="text-[#00B4D8] font-medium">A · {left.result?.skill_level_detected || t('compare.sessionFallback')}</span>
              <span className="text-muted-foreground flex items-center gap-1"><Minus className="w-3 h-3 rotate-90" /> vs</span>
              <span className="text-[#FF6B35] font-medium">B · {right.result?.skill_level_detected || t('compare.sessionFallback')}</span>
            </div>
          </div>
        </>
      )}

      {!loadingPair && (!left || !right) && list && (list.length + refsAvailable.length) >= 2 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="compare-empty">
          <Columns2 className="w-10 h-10 mx-auto opacity-40" />
          <p className="mt-3">{t('compare.chooseTwo')}</p>
        </div>
      )}

      <Link to="/sessions" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white mt-8"><ArrowLeft className="w-4 h-4" /> {t('compare.backToSessions')}</Link>
    </div>
  );
}
